import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const { t } = useTranslation();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim()) {
      alert('感谢您的提交！');
      form.reset();
      return;
    }
    const formData = new FormData(form);
    fetch('https://readdy.ai/api/form/d9s8kdnnahb7382ovs6g', {
      method: 'POST',
      body: new URLSearchParams(formData as never).toString(),
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
      .then(async (res) => {
        const text = await res.text();
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        if (res.ok && parsed?.code === 'OK') {
          alert('感谢您的留言，我们会尽快回复！');
          form.reset();
        } else {
          const msg = parsed?.meta?.message || parsed?.message || '提交失败，请稍后重试';
          alert(msg);
        }
      })
      .catch(() => {
        alert('网络错误，请稍后重试');
      });
  };

  return (
    <>
      <ScrollToTop />
      <Navbar />

      <main>
        <Breadcrumb />

        <section className="w-full pt-8 pb-14 md:pb-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-14">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
                {t('about.title')}
              </h1>
              <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto">
                麻将在线玩致力于为全球麻将爱好者提供最优质的在线麻将体验，让每一位玩家都能在这里找到属于自己的麻将乐趣。
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 mb-16">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-5">
                  {t('about.brandIntro')}
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    麻将在线玩创立于2024年，是一个专注于提供高质量在线麻将体验的平台。我们的使命是让每一位麻将爱好者都能随时随地享受到正宗、纯粹的麻将乐趣。
                  </p>
                  <p>
                    我们深知麻将不仅仅是一种游戏，更是中华民族传统文化的重要组成部分。每一张牌、每一个规则背后，都蕴含着深厚的文化底蕴和智慧结晶。我们致力于在保留传统麻将精髓的同时，利用现代技术让麻将焕发新的活力。
                  </p>
                  <p>
                    目前，我们的平台已支持国标麻将、四川麻将、广东麻将等多种主流玩法，累计服务超过百万麻将爱好者。无论你是竞技型玩家还是休闲娱乐玩家，都能在这里找到适合自己的玩法。
                  </p>
                  <div className="grid grid-cols-3 gap-4 mt-6">
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="text-2xl font-heading font-bold text-primary-600">100万+</div>
                      <div className="text-xs text-foreground-500 mt-1">注册用户</div>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="text-2xl font-heading font-bold text-primary-600">10+</div>
                      <div className="text-xs text-foreground-500 mt-1">麻将玩法</div>
                    </div>
                    <div className="text-center p-4 bg-background-100 rounded-lg">
                      <div className="text-2xl font-heading font-bold text-primary-600">99.9%</div>
                      <div className="text-xs text-foreground-500 mt-1">服务可用率</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="relative h-64 lg:h-auto rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20and%20inviting%20Chinese%20teahouse%20interior%20with%20mahjong%20table%20and%20traditional%20decor%20elements%2C%20soft%20natural%20lighting%20through%20wooden%20windows%2C%20peaceful%20atmosphere%2C%20elegant%20composition%2C%20minimalist%20photography&width=800&height=500&seq=mahjong-about-01&orientation=landscape"
                  alt="麻将在线玩 - 品牌介绍"
                  className="w-full h-full object-cover object-top rounded-lg"
                  loading="lazy"
                />
              </div>
            </div>

            <div className="mb-16">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                {t('about.reputation')}
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {[
                  { icon: 'ri-shield-check-line', title: '安全可靠', desc: '采用银行级加密技术保护用户数据和资金安全' },
                  { icon: 'ri-scales-line', title: '公平公正', desc: '随机发牌算法经第三方认证，杜绝任何作弊可能' },
                  { icon: 'ri-customer-service-2-line', title: '优质服务', desc: '7×24小时客服支持，快速响应每一位玩家的需求' },
                  { icon: 'ri-thumb-up-line', title: '口碑信赖', desc: '超过百万用户的共同选择，好评率高达98%' },
                ].map((item, i) => (
                  <div key={i} className="p-5 bg-background-100 rounded-lg text-center">
                    <span className="w-12 h-12 flex items-center justify-center mx-auto rounded-full bg-accent-100 text-accent-700 mb-4">
                      <i className={`${item.icon} text-xl`}></i>
                    </span>
                    <h3 className="text-base font-heading font-semibold text-foreground-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8 text-center">
                {t('about.contact')}
              </h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 max-w-5xl mx-auto">
                <div className="space-y-5">
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-mail-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h3>
                      <p className="text-sm text-foreground-500">contact@majiangzaxianwan.com</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-phone-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">客服电话</h3>
                      <p className="text-sm text-foreground-500">400-888-6666</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-map-pin-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">公司地址</h3>
                      <p className="text-sm text-foreground-500">中国北京市朝阳区建国路88号</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-700">
                      <i className="ri-time-line text-lg"></i>
                    </span>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">服务时间</h3>
                      <p className="text-sm text-foreground-500">周一至周日 9:00 - 22:00</p>
                    </div>
                  </div>
                </div>

                <div className="bg-background-100 rounded-lg p-6">
                  <h3 className="text-lg font-heading font-semibold text-foreground-900 mb-5">
                    给我们留言
                  </h3>
                  <form data-readdy-form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="name" className="block text-sm text-foreground-700 mb-1.5">
                        姓名
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm text-foreground-700 mb-1.5">
                        邮箱
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm text-foreground-700 mb-1.5">
                        留言内容
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-3 py-2 text-sm rounded-md border border-background-200 bg-background-50 text-foreground-800 focus:outline-none focus:border-primary-300 transition-colors duration-200 resize-none"
                        placeholder="请输入您的留言内容（最多500字）"
                      ></textarea>
                    </div>
                    <input
                      type="text"
                      name="phone_alt"
                      className="hp-field"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      readOnly
                    />
                    <button
                      type="submit"
                      className="w-full px-6 py-2.5 rounded-md bg-primary-500 text-white text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors duration-200 cursor-pointer"
                    >
                      提交留言
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "麻将在线玩",
            "url": "https://majiangzaxianwan.com/",
            "description": "免费畅玩正宗麻将游戏，支持多种麻将玩法",
            "email": "contact@majiangzaxianwan.com",
            "telephone": "+86-400-888-6666",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "建国路88号",
              "addressLocality": "北京",
              "addressRegion": "朝阳区",
              "addressCountry": "CN",
            },
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer service",
              "telephone": "+86-400-888-6666",
              "availableLanguage": ["Chinese"],
            },
          }),
        }}
      />
    </>
  );
}